import { NavLink } from "react-router-dom";
import { useCurrentUser } from "../../../hooks/useGetUserDetail";
import "./WebHome.scss";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function WebHome() {
  const { data: userInfo, loading } = useCurrentUser();

  const firstName =
    userInfo?.firstName || userInfo?.name?.split(" ")[0] || "there";

  const school = userInfo?.schoolName || userInfo?.school?.name || null;

  const role = userInfo?.profileType
    ? userInfo.profileType.charAt(0).toUpperCase() +
      userInfo.profileType.slice(1)
    : null;

  return (
    <div className="web-home">
      {/* Banner */}
      <section className="web-home__banner">
        <div className="web-home__banner-text">
          <p className="web-home__greeting">{getGreeting()},</p>
          <h1 className="web-home__name">
            {loading ? "Loading…" : firstName} 👋
          </h1>
          {school && <p className="web-home__school">{school}</p>}
        </div>
        {role && <span className="web-home__role-badge">{role}</span>}
      </section>

      {/* Quick stats */}
      <section className="web-home__stats">
        <div className="web-home__stat-card web-home__stat-card--purple">
          <div className="web-home__stat-icon">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <circle
                cx="13"
                cy="13"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M13 7v6l3.5 2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="web-home__stat-info">
            <span className="web-home__stat-label">Session</span>
            <span className="web-home__stat-value">Active Now</span>
          </div>
        </div>

        <div className="web-home__stat-card web-home__stat-card--blue">
          <div className="web-home__stat-icon">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <rect
                x="3"
                y="5"
                width="20"
                height="16"
                rx="3"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
              <path d="M3 10h20" stroke="currentColor" strokeWidth="2" />
              <path
                d="M8 15h3M14 15h5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="web-home__stat-info">
            <span className="web-home__stat-label">Mode</span>
            <span className="web-home__stat-value">WebApp</span>
          </div>
        </div>

        <div className="web-home__stat-card web-home__stat-card--green">
          <div className="web-home__stat-icon">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <circle
                cx="13"
                cy="9"
                r="4"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M5 21c0-4.418 3.582-8 8-8s8 3.582 8 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </div>
          <div className="web-home__stat-info">
            <span className="web-home__stat-label">Profile</span>
            <span className="web-home__stat-value">
              {loading ? "—" : role || "Active"}
            </span>
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="web-home__actions-section">
        <h2 className="web-home__section-title">Quick Actions</h2>
        <div className="web-home__actions">
          <NavLink to="/web/appJourneyPage" className="web-home__action-card">
            <div className="web-home__action-icon web-home__action-icon--purple">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="8"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M4 20c0-4.418 3.582-8 8-8s8 3.582 8 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </div>
            <span className="web-home__action-label">View Profile</span>
            <span className="web-home__action-arrow">→</span>
          </NavLink>

          <div className="web-home__action-card web-home__action-card--muted">
            <div className="web-home__action-icon web-home__action-icon--grey">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 4h16v12H4z"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 20h8M12 16v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <span className="web-home__action-label">Dashboard</span>
            <span className="web-home__action-tag">Soon</span>
          </div>

          <div className="web-home__action-card web-home__action-card--muted">
            <div className="web-home__action-icon web-home__action-icon--grey">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 8h1a4 4 0 0 1 0 8h-1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="6"
                  y1="1"
                  x2="6"
                  y2="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="10"
                  y1="1"
                  x2="10"
                  y2="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="14"
                  y1="1"
                  x2="14"
                  y2="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <span className="web-home__action-label">Activity</span>
            <span className="web-home__action-tag">Soon</span>
          </div>
        </div>
      </section>

      {/* Getting started */}
      <section className="web-home__feed">
        <h2 className="web-home__section-title">Getting Started</h2>
        <div className="web-home__feed-card">
          <div className="web-home__feed-item">
            <div className="web-home__feed-dot web-home__feed-dot--purple" />
            <div className="web-home__feed-content">
              <span className="web-home__feed-title">Welcome to WebApp</span>
              <span className="web-home__feed-sub">
                Use the navigation above to explore your experience
              </span>
            </div>
          </div>
          <div className="web-home__feed-item">
            <div className="web-home__feed-dot web-home__feed-dot--blue" />
            <div className="web-home__feed-content">
              <span className="web-home__feed-title">View your Profile</span>
              <span className="web-home__feed-sub">
                Check your account details and role information
              </span>
            </div>
          </div>
          <div className="web-home__feed-item">
            <div className="web-home__feed-dot web-home__feed-dot--green" />
            <div className="web-home__feed-content">
              <span className="web-home__feed-title">
                More features coming soon
              </span>
              <span className="web-home__feed-sub">
                New screens and capabilities are on the way
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default WebHome;
